import { isConditionalOption } from 'src/helpers/question-helper';
import {
  ContentLayout,
  IQuestionScheme,
  ISurveyContentBlock,
  ISurveyQuestion,
  QuestionType,
  QuestionCreationDto,
  QuestionScheme,
  SkipOptions,
  SurveyContentBlock,
  SurveyOption,
  SurveyQuestion,
} from 'src/services/api/api-client';

const normalizeDtoBlocks = (blocks: ISurveyContentBlock[] | null | undefined): SurveyContentBlock[] =>
  (blocks ?? []).map((block) =>
    SurveyContentBlock.fromJS({
      titleText: block.titleText ?? '',
      descriptionText: block.descriptionText ?? '',
      image: block.image?.resourceKey
        ? {
            resourceKey: block.image.resourceKey,
            accessibilityLabel: block.image.accessibilityLabel ?? null,
          }
        : null,
      layout: block.layout ?? ContentLayout.Default,
    }),
  );

export function isIQuestionScheme(x: any | IQuestionScheme): x is IQuestionScheme {
  return (x as IQuestionScheme).version !== undefined && (x as IQuestionScheme).description !== undefined;
}

export const toCreateQuestionDto = (question: ISurveyQuestion): QuestionCreationDto => {
  const isContentQuestion =
    question.questionType === QuestionType.Content || question.questionType === QuestionType.Gallery;
  const normalizedOptions = isContentQuestion
    ? [
        SurveyOption.fromJS({
          discriminator: 'CommonOption',
          optionText: '',
          optionValue: '__presentation__',
          scoreValue: 0,
          answerValue: undefined,
          defaultValue: undefined,
          questionScheme: null,
        }),
      ]
    : question.questionType === QuestionType.FreeInput
    ? [
        SurveyOption.fromJS({
          discriminator: 'CommonOption',
          optionText: '',
          optionValue: '0',
          scoreValue: question.options?.[0]?.scoreValue ?? 0,
          answerValue: undefined,
          defaultValue: undefined,
          questionScheme: null,
        }),
      ]
    : question.options!.map((option) =>
        SurveyOption.fromJS({
          discriminator: option.discriminator,
          optionText: option.optionText,
          optionValue: option.optionValue,
          answerValue: option.answerValue,
          defaultValue: option.defaultValue,
          scoreValue: option.scoreValue,
          questionScheme: option.questionScheme,
        }),
      );

  return new QuestionCreationDto({
    questionType: question.questionType,
    questionText: question.questionText,
    isSkippedRender: question.isSkippedRender ?? SkipOptions.NotSkip,
    blocks: isContentQuestion ? normalizeDtoBlocks(question.contentBlock) : null,
    options: normalizedOptions,
    canBeSkipped: question.canBeSkipped,
    withPreviousAnswer: question.withPreviousAnswer ?? null,
    attentionText: question.attentionText ?? null,
    attentionTextVariant: question.attentionTextVariant,
    firsOptionIsPositive: !!question.firsOptionIsPositive,
  });
};

export const getQuestionById = (scheme: IQuestionScheme, id: string): ISurveyQuestion | null => {
  if (!scheme.questions) {
    return null;
  }

  for (const question of scheme.questions) {
    if (question.id === id) {
      return question;
    }

    if (!question.options) {
      continue;
    }

    for (const option of question.options) {
      if (isConditionalOption(option) && option.questionScheme) {
        const result = getQuestionById(option.questionScheme, id);

        if (!result) {
          continue;
        }

        return result;
      }
    }
  }

  return null;
};

export const removeQuestionById = (scheme: IQuestionScheme, id: string): IQuestionScheme => {
  const schemeCopy = Object.assign(Object.create(Object.getPrototypeOf(scheme)), scheme) as IQuestionScheme;

  if (!schemeCopy.questions) {
    return scheme;
  }

  for (const question of schemeCopy.questions) {
    if (question.id === id) {
      schemeCopy.questions = schemeCopy?.questions?.filter((x: ISurveyQuestion) => x.id !== id);
    }

    if (!question.options) {
      continue;
    }

    for (const option of question.options) {
      if (isConditionalOption(option) && option.questionScheme) {
        const result = removeQuestionById(option.questionScheme, id);

        if (result === option.questionScheme) {
          continue;
        }
        option.questionScheme = result as QuestionScheme;
      }
    }
  }

  return schemeCopy;
};

export const pasteQuestionAfter = (
  scheme: IQuestionScheme,
  id: string,
  newQuestion: SurveyQuestion,
): IQuestionScheme | null => {
  const schemeCopy = Object.assign(Object.create(Object.getPrototypeOf(scheme)), scheme);

  if (!schemeCopy.questions) {
    return schemeCopy as IQuestionScheme;
  }

  for (const question of schemeCopy.questions) {
    if (question.id === id) {
      schemeCopy.questions = [...(schemeCopy?.questions ?? []), newQuestion];
    }

    if (!question.options) {
      continue;
    }

    for (const option of question.options) {
      if (isConditionalOption(option) && option.questionScheme) {
        const result = pasteQuestionAfter(option.questionScheme, id, newQuestion);

        if (result === option.questionScheme) {
          continue;
        }
        option.questionScheme = result as QuestionScheme;
      }
    }
  }

  return schemeCopy as IQuestionScheme;
};

export const moveQuestion = (scheme: IQuestionScheme, id: string, direction: 'Up' | 'Down'): IQuestionScheme => {
  const schemeCopy = Object.assign(Object.create(Object.getPrototypeOf(scheme)), scheme) as IQuestionScheme;

  if (!schemeCopy.questions) {
    return schemeCopy;
  }

  for (const question of schemeCopy.questions) {
    if (question.id === id) {
      const curQuestionIndex = scheme?.questions?.findIndex((x) => x.id === id);
      if (curQuestionIndex === undefined || curQuestionIndex === -1) {
        return schemeCopy;
      }

      const newQuestions = Array.from(schemeCopy?.questions, (q, i) => {
        if (schemeCopy?.questions && i === curQuestionIndex) {
          return schemeCopy?.questions[i - (direction === 'Up' ? 1 : -1)];
        }

        if (schemeCopy?.questions && i === curQuestionIndex - (direction === 'Up' ? 1 : -1)) {
          return schemeCopy?.questions[curQuestionIndex];
        }

        return q;
      });

      schemeCopy.questions = newQuestions;
      return schemeCopy;
    }

    if (!question.options) {
      continue;
    }

    for (const option of question.options) {
      if (isConditionalOption(option) && option.questionScheme) {
        const result = moveQuestion(option.questionScheme, id, direction);

        if (result === option.questionScheme) {
          continue;
        }
        option.questionScheme = result as QuestionScheme;
      }
    }
  }

  return schemeCopy;
};

export const reorderRootQuestions = (scheme: IQuestionScheme, sourceId: string, targetId: string): IQuestionScheme => {
  const schemeCopy = Object.assign(Object.create(Object.getPrototypeOf(scheme)), scheme) as IQuestionScheme;
  if (!schemeCopy.questions?.length || sourceId === targetId) {
    return schemeCopy;
  }

  const questions = [...schemeCopy.questions];
  const sourceIndex = questions.findIndex((question) => question.id === sourceId);
  const targetIndex = questions.findIndex((question) => question.id === targetId);

  if (sourceIndex < 0 || targetIndex < 0) {
    return schemeCopy;
  }

  const [movedQuestion] = questions.splice(sourceIndex, 1);
  questions.splice(targetIndex, 0, movedQuestion);
  schemeCopy.questions = questions;
  return schemeCopy;
};
